import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../contexts/StoreProvider';
import alertify from 'alertifyjs';

const CheckoutScreen = () => {
  const { cart, getCartTotal, clearCart, regions } = useStore();
  const navigate = useNavigate();

  const [fulfillmentMethod, setFulfillmentMethod] = useState('DELIVERY');
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [isAddingNewAddress, setIsAddingNewAddress] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({ title: '', address_line: '', city: '', region: '' });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Fetch existing addresses
  React.useEffect(() => {
    const fetchAddresses = async () => {
      const token = localStorage.getItem('emba_access');
      if (token) {
        try {
          const res = await fetch(`${API_BASE}/api/store/addresses/`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            const addressList = Array.isArray(data) ? data : (data.results || []);
            setAddresses(addressList);
            if (addressList.length > 0) {
              setSelectedAddressId(addressList[0].id);
            } else {
              setIsAddingNewAddress(true);
            }
          }
        } catch (e) {
          console.error(e);
        }
      }
    };
    fetchAddresses();
  }, [API_BASE]);

  if (cart.length === 0) {
    navigate('/store');
    return null;
  }

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    const token = localStorage.getItem('emba_access');
    const userStr = localStorage.getItem('emba_user');
    
    if (!token || !userStr) {
      alertify.error("Please login to complete checkout");
      navigate('/login?redirect=/store/checkout');
      return;
    }
    
    const user = JSON.parse(userStr);
    
    let finalShippingAddress = '';
    let finalToken = token;

    if (fulfillmentMethod === 'DELIVERY') {
      const selected = addresses.find(a => a.id === selectedAddressId);
      if (!selected) {
        alertify.error("Please select a shipping address or add a new one");
        return;
      }
      finalShippingAddress = `${selected.title ? selected.title + ' - ' : ''}${selected.address_line}${selected.city ? ', ' + selected.city : ''}`;
    }

    setIsSubmitting(true);
    try {
      const payload = { 
        fulfillment_method: fulfillmentMethod,
        return_url: `${window.location.origin}/store/orders` // Backend will append /{order.id}
      };
      if (fulfillmentMethod === 'DELIVERY') {
        payload.shipping_address = selectedAddressId;
      }

      const res = await fetch(`${API_BASE}/api/store/cart/checkout/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok || res.status === 201) {
        const orderData = await res.json();
        clearCart();
        if (orderData.checkout_url) {
          window.location.href = orderData.checkout_url;
        } else {
          navigate('/store'); 
          alertify.success("Order placed successfully");
        }
      } else {
        alertify.error("Failed to checkout");
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error(err);
      alertify.error("An error occurred during checkout");
      setIsSubmitting(false);
    }
  };

  const handleSaveNewAddress = async () => {
    if (!newAddress.address_line.trim()) {
      alertify.error("Please enter a street address");
      return;
    }

    const token = localStorage.getItem('emba_access');
    if (!token) return;

    setIsSavingAddress(true);
    try {
      const res = await fetch(`${API_BASE}/api/store/addresses/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          title: newAddress.title || 'Other',
          address_line: newAddress.address_line,
          city: newAddress.city,
          region: newAddress.region || null
        })
      });

      if (res.ok) {
        const savedAddress = await res.json();
        setAddresses([savedAddress, ...addresses]);
        setSelectedAddressId(savedAddress.id);
        setIsAddingNewAddress(false);
        setNewAddress({ title: '', address_line: '', city: '', region: '' });
        alertify.success("Address saved successfully!");
      } else {
        alertify.error("Failed to save address");
      }
    } catch (e) {
      console.error(e);
      alertify.error("An error occurred while saving the address");
    } finally {
      setIsSavingAddress(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-12">
      {/* Checkout Form */}
      <div className="flex-1">
        <h1 className="text-2xl font-bold tracking-widest uppercase mb-8 border-b pb-4">Checkout</h1>
        
        <form onSubmit={handleCheckout} className="space-y-8">
          {/* Fulfillment Method */}
          <div>
            <h3 className="text-lg font-bold tracking-wide uppercase mb-4">Fulfillment Method</h3>
            <div className="flex gap-4">
              <label className="flex-1 cursor-pointer">
                <input 
                  type="radio" 
                  className="peer sr-only"
                  name="fulfillment" 
                  value="DELIVERY" 
                  checked={fulfillmentMethod === 'DELIVERY'}
                  onChange={() => setFulfillmentMethod('DELIVERY')}
                />
                <div className="p-4 border-2 border-gray-200 text-center font-semibold tracking-wider uppercase peer-checked:border-black peer-checked:bg-black peer-checked:text-white hover:border-gray-300 transition-colors">
                  Delivery
                </div>
              </label>
              <label className="flex-1 cursor-pointer">
                <input 
                  type="radio" 
                  className="peer sr-only"
                  name="fulfillment" 
                  value="PICKUP" 
                  checked={fulfillmentMethod === 'PICKUP'}
                  onChange={() => setFulfillmentMethod('PICKUP')}
                />
                <div className="p-4 border-2 border-gray-200 text-center font-semibold tracking-wider uppercase peer-checked:border-black peer-checked:bg-black peer-checked:text-white hover:border-gray-300 transition-colors">
                  Pickup
                </div>
              </label>
            </div>
          </div>

          {/* Shipping Address Section */}
          {fulfillmentMethod === 'DELIVERY' && (
            <div className="animate-in fade-in slide-in-from-top-4 duration-300">
              <h3 className="text-lg font-bold tracking-wide uppercase mb-4">Shipping Address</h3>
              
              {addresses.length > 0 && !isAddingNewAddress && (
                <div className="space-y-4 mb-4">
                  {addresses.map(addr => (
                    <label key={addr.id} className="flex p-4 border border-gray-200 cursor-pointer hover:border-black transition-colors">
                      <input 
                        type="radio" 
                        name="address" 
                        className="mt-1 mr-4"
                        checked={selectedAddressId === addr.id}
                        onChange={() => setSelectedAddressId(addr.id)}
                      />
                      <div>
                        <div className="font-bold tracking-widest uppercase text-sm mb-1">{addr.title}</div>
                        <div className="text-gray-600">{addr.address_line}</div>
                        {addr.city && <div className="text-gray-500 text-sm">{addr.city}</div>}
                        {addr.region_detail && <div className="text-gray-500 text-sm">{addr.region_detail.name} (Delivery: ₵{addr.region_detail.delivery_fee})</div>}
                      </div>
                    </label>
                  ))}
                  <button 
                    type="button"
                    onClick={() => setIsAddingNewAddress(true)}
                    className="text-sm font-bold uppercase tracking-wider underline hover:text-gray-600"
                  >
                    + Add New Address
                  </button>
                </div>
              )}

              {(isAddingNewAddress || addresses.length === 0) && (
                <div className="space-y-4 bg-gray-50 p-6 border border-gray-200">
                  <h4 className="font-bold tracking-widest uppercase text-sm mb-2">New Address Details</h4>
                  
                  <input
                    type="text"
                    name="title"
                    placeholder="Address Title (e.g. Home, Office)"
                    value={newAddress.title}
                    onChange={(e) => setNewAddress({...newAddress, title: e.target.value})}
                    className="w-full p-3 border border-gray-300 focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                  />
                  
                  <textarea
                    name="address_line"
                    value={newAddress.address_line}
                    onChange={(e) => setNewAddress({...newAddress, address_line: e.target.value})}
                    placeholder="Street Address, Suburb, GPS Address"
                    className="w-full p-3 border border-gray-300 focus:outline-none focus:border-black focus:ring-1 focus:ring-black min-h-[100px]"
                    required={fulfillmentMethod === 'DELIVERY'}
                  ></textarea>
                  
                  <input
                    type="text"
                    name="city"
                    placeholder="City"
                    value={newAddress.city}
                    onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                    className="w-full p-3 border border-gray-300 focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                  />
                  
                  <select
                    value={newAddress.region}
                    onChange={(e) => setNewAddress({...newAddress, region: e.target.value})}
                    className="w-full p-3 border border-gray-300 focus:outline-none focus:border-black focus:ring-1 focus:ring-black bg-white"
                    required={fulfillmentMethod === 'DELIVERY'}
                  >
                    <option value="">Select Region</option>
                    {regions && regions.map(r => (
                      <option key={r.id} value={r.id}>{r.name} - Delivery: ₵{r.delivery_fee}</option>
                    ))}
                  </select>
                  
                  
                  <div className="flex gap-4 mt-4">
                    <button 
                      type="button"
                      onClick={handleSaveNewAddress}
                      disabled={isSavingAddress}
                      className="bg-black text-white px-6 py-2 font-bold tracking-widest uppercase hover:bg-gray-800 transition-colors disabled:bg-gray-400"
                    >
                      {isSavingAddress ? 'Saving...' : 'Save Address'}
                    </button>
                    {addresses.length > 0 && (
                      <button 
                        type="button"
                        onClick={() => setIsAddingNewAddress(false)}
                        className="px-6 py-2 text-sm font-bold uppercase tracking-wider underline text-gray-500 hover:text-black"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {fulfillmentMethod === 'PICKUP' && (
            <div className="animate-in fade-in slide-in-from-top-4 duration-300 bg-gray-50 p-6 border border-gray-200">
              <h3 className="text-sm font-bold tracking-wide uppercase mb-2">Pickup Location</h3>
              <p className="text-gray-600">You can pick up your order from the EMBA main office during working hours.</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-black text-white py-4 font-bold tracking-widest uppercase hover:bg-gray-900 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Processing...' : 'Pay & Complete Order'}
          </button>
        </form>
      </div>

      {/* Cart Summary */}
      <div className="w-full md:w-96 flex-shrink-0">
        <div className="bg-gray-50 p-6 border border-gray-200 sticky top-24">
          <h2 className="text-lg font-bold tracking-widest uppercase mb-6 pb-4 border-b border-gray-300">Your Order</h2>
          <div className="space-y-6 mb-6 max-h-[60vh] overflow-y-auto pr-2">
            {cart.map(item => (
              <div key={item.product.id} className="flex gap-4 items-center">
                <div className="w-20 h-28 bg-gray-200 flex-shrink-0">
                  {item.product.images && item.product.images.length > 0 ? (
                    <img src={item.product.images[0].image} alt={item.product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-[10px]">No Img</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold tracking-[0.2em] text-gray-500 uppercase mb-1">
                    {item.product.category_detail?.name || 'GENERAL'}
                  </div>
                  <div className="font-bold text-sm text-black truncate">{item.product.name}</div>
                  {item.variant_detail && (
                    <div className="text-gray-500 text-[11px] mt-0.5">Size: {item.variant_detail.size}</div>
                  )}
                  <div className="text-gray-500 text-xs mt-1">Qty: {item.quantity}</div>
                </div>
                <div className="font-semibold text-black text-sm">
                  ₵{((item.variant_detail ? parseFloat(item.variant_detail.price) : parseFloat(item.product.price)) * item.quantity).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-300 pt-4 space-y-2">
            <div className="flex justify-between items-center text-gray-600">
              <span className="uppercase tracking-wider text-sm">Subtotal</span>
              <span className="font-semibold">₵{getCartTotal().toFixed(2)}</span>
            </div>
            {fulfillmentMethod === 'DELIVERY' && selectedAddressId && (
              <div className="flex justify-between items-center text-gray-600">
                <span className="uppercase tracking-wider text-sm">Delivery Fee</span>
                <span className="font-semibold">
                  ₵{(
                    parseFloat(addresses.find(a => a.id === selectedAddressId)?.region_detail?.delivery_fee || 0)
                  ).toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex justify-between items-center pt-2 border-t border-gray-200 mt-2">
              <span className="font-bold uppercase tracking-wider">Total</span>
              <span className="text-xl font-bold text-black">
                ₵{(
                  getCartTotal() + 
                  (fulfillmentMethod === 'DELIVERY' && selectedAddressId 
                    ? parseFloat(addresses.find(a => a.id === selectedAddressId)?.region_detail?.delivery_fee || 0) 
                    : 0)
                ).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutScreen;
